import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

/**
 * Shared "Your Tasks" mutation handlers — checkbox/snooze/dismiss, plus the
 * optimistic-removal primitive they're all built on — for MyHomePM.js and
 * SupplierPortal.js's Your Tasks section (TaskList.js). Both pages used to
 * hand-copy an identical implementation (2026-09-01, real duplication found
 * via code review) differing only in how each stores its own tasks array
 * and what it calls to reload on failure. This hook owns the one real
 * implementation; each page supplies its own updateTasks/reload so the hook
 * never needs to know the caller's actual state shape.
 *
 * Optimistic: the task leaves the list immediately rather than re-fetching
 * (and flashing/full-page-loading) the whole payload for a one-item change.
 * On any failed PATCH, `reload()` re-fetches for real so the list reflects
 * what's actually true server-side — an alert() plus reload, not a silent
 * failure, since the optimistic removal already told the user it worked.
 *
 * @param {(updater: (tasks: any[]) => any[]) => void} updateTasks — called
 *   with a `tasks => tasks` updater function; the caller decides how that
 *   folds into its own state shape (a bare tasks array, or a field nested
 *   inside a larger object).
 * @param {() => void} reload — the caller's own re-fetch-everything function.
 */
export function useTaskActions(updateTasks, reload) {
  const removeTaskLocally = (itemId) => {
    updateTasks(tasks => tasks.filter(t => t.id !== itemId));
  };

  const handleToggleCompleted = (itemId, completed) => {
    removeTaskLocally(itemId);
    axios.patch(`${API}/week-board/items/${itemId}`, { completed })
      .catch(() => { alert('Failed to update.'); reload(); });
  };

  const handleSnooze = (itemId, untilIso) => {
    removeTaskLocally(itemId);
    axios.patch(`${API}/week-board/items/${itemId}/dismiss`, { snoozed_until: untilIso })
      .catch(() => { alert('Failed to snooze.'); reload(); });
  };

  const handleDismiss = (itemId) => {
    removeTaskLocally(itemId);
    axios.patch(`${API}/week-board/items/${itemId}/dismiss`, { snoozed_until: null })
      .catch(() => { alert('Failed to dismiss.'); reload(); });
  };

  return { removeTaskLocally, handleToggleCompleted, handleSnooze, handleDismiss };
}
